<template>
    <div class="container">
        <back-button location="/initiatives"></back-button>

        <h1 class="title">Graduate Boot Camp</h1>
        <div class="content">
            <div class="introduction">
                <p>
                    The Entelect Graduate Boot Camp is a seven week long interactive training programme designed to make
                    our graduates industry-ready. The relationships they build in the Boot Camp form a basis for their
                    network that accelerates their journey as software engineers.
                </p>
                <p class="fun-fact">
                    The Boot Camp experience shook me to my core and made me question myself at every turn, but I’m so grateful to have been a part of it.
                    <span class="author">- Sarah Jenkins</span>
                </p>
                <p class="fun-fact">
                    Boot Camp is like the final year of your degree + getting a year’s experience & stress, all rolled up into 6 weeks - It was horribly amazing.
                    <span class="author">- Jean-Pierre Roussel</span>
                </p>
            </div>

            <div class="images">
                <div class="image-1 image-border"
                     :style="{ 'background-image': `url(${images.trainingCentreBalloons})` }"></div>
                <div class="image-2 image-border inverse"
                     :style="{ 'background-image': `url(${images.trainingCentreHats})` }"></div>
                <div class="image-3 image-border inverse"
                     :style="{ 'background-image': `url(${images.mainBoardroom})` }"></div>
            </div>

            <div class="stats">
                <stats-icon :icon-src="statsImages.graduates" stat="41" title="Graduates"></stats-icon>
                <stats-icon :icon-src="statsImages.hoursOfWork" stat=">14K" title="Hours of work"></stats-icon>
                <stats-icon :icon-src="statsImages.lecturers" stat="16" title="Lecturers"></stats-icon>
                <stats-icon :icon-src="statsImages.talks" stat="26" title="Talks"></stats-icon>
                <stats-icon :icon-src="statsImages.assignments" stat="45" title="Assignments"></stats-icon>
                <stats-icon :icon-src="statsImages.technologies" stat="27" title="Technologies"></stats-icon>
                <stats-icon :icon-src="statsImages.commits" stat=">6K" title="Commits"></stats-icon>
                <stats-icon :icon-src="statsImages.builds" stat="864" title="Builds"></stats-icon>
            </div>
        </div>
    </div>
</template>

<script>
    import BackButton from "../../widgets/BackButton";
    import StatsIcon from '../../widgets/StatsIcon';

    // Main images imports
    import trainingCentreBalloons from '../../assets/images/Bootcamp/training-centre-balloons.png';
    import mainBoardroom from '../../assets/images/Bootcamp/main-boardroom.png';
    import trainingCentreHats from '../../assets/images/Bootcamp/training-centre-hats.jpg';

    // Stats images imports
    import graduatesImage from '../../assets/images/Bootcamp/graduation-hat.png';
    import hoursOfWorkImage from '../../assets/images/Bootcamp/hourglass.png';
    import lecturersImage from '../../assets/images/Bootcamp/student.png';
    import talksImage from '../../assets/images/Bootcamp/notebook.png';
    import assignmentsImage from '../../assets/images/Bootcamp/projection-screen.png';
    import technologiesImage from '../../assets/images/Bootcamp/circuit.png';
    import commitsImage from '../../assets/images/Bootcamp/git-logo.png';
    import buildsImage from '../../assets/images/Bootcamp/gears.png';

    export default {
        components: {
            BackButton,
            StatsIcon
        },
        data: function () {
            return {
                images: {
                    trainingCentreBalloons: trainingCentreBalloons,
                    mainBoardroom: mainBoardroom,
                    trainingCentreHats: trainingCentreHats,
                },
                statsImages: {
                    graduates: graduatesImage,
                    hoursOfWork: hoursOfWorkImage,
                    lecturers: lecturersImage,
                    talks: talksImage,
                    assignments: assignmentsImage,
                    technologies: technologiesImage,
                    commits: commitsImage,
                    builds: buildsImage,
                }
            };

        },
        methods: {
            track () {
                this.$ga.page('/2018/initiatives/bootcamp') // This is for Google Analytics tracking
            }
        }
    }
</script>

<style lang="scss" scoped>
    .container {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: auto 1fr;
        grid-template-areas: 'back title  .' 'content content content';
        color: white;
        background-size: cover;
        background-position: center;
        padding-bottom: 48px;

        @media only screen and (max-width: $screen-sm-max) {
            grid-template-columns: 1fr 2fr 1fr;
        }

        .title {
            grid-area: title;
            align-self: start;
            justify-self: center;
            margin: 24px;
            display: grid;
            align-content: center;
            justify-content: center;
        }

        .content {
            grid-area: content;
            display: grid;
            grid-template-areas: '. introduction stats .' '. images images .';
            grid-gap: 32px;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            grid-template-rows: auto auto auto;

            @media only screen and (max-width: $screen-md-max) {
                grid-template-columns: 1fr 2fr 2fr 1fr;
                grid-template-areas:
                        '. introduction introduction .'
                        '. stats stats .'
                        '. images images .';
            }

            @media only screen and (max-width: $screen-sm-max) {
                grid-template-columns: 1fr 3fr 3fr 1fr;
            }

            @media only screen and (max-width: $screen-xs-max) {
                grid-template-columns: 32px 1fr 32px;
                grid-gap: 0;
                grid-template-areas:
                        '. introduction .'
                        '. stats .'
                        '. images .';
            }

            .introduction {
                grid-area: introduction;
                text-align: justify;
                font-size: 16px;
                display: grid;
                grid-gap: 24px;
                grid-template-columns: 1fr;

                @media only screen and (max-width: $screen-md-max) {
                    grid-template-columns: 1fr 1fr;

                    p:not(.fun-fact) {
                        grid-column: 1 / span 2;
                    }
                }

                @media only screen and (max-width: $screen-sm-max) {
                    grid-template-columns: 1fr;

                    p:not(.fun-fact) {
                        grid-column: initial;
                    }
                }

                .fun-fact {
                    margin: 32px auto;

                    @media only screen and (min-width: $screen-md-min) and (max-width: $screen-md-max) {
                        margin: 0 auto;
                    }
                }
            }

            .images {
                grid-area: images;

                display: grid;
                grid-template-columns: 1fr 1fr 1fr;
                grid-template-rows: 200px 200px;
                grid-template-areas:
                        'image-1 image-1 image-3'
                        'image-2 image-2 image-3';

                @media only screen and (max-width: $screen-md-max) {
                    grid-template-columns: 1fr 1fr;
                    grid-template-rows: 200px 300px 200px;
                    grid-template-areas:
                            'image-1 image-1'
                            'image-3 image-2'
                            'image-3 image-2';
                }

                @media only screen and (max-width: $screen-xs-max) {
                    grid-template-columns: 1fr;
                    grid-template-rows: 200px 200px 200px;
                    grid-template-areas:
                            'image-1'
                            'image-2'
                            'image-3';
                }

                grid-gap: 24px;

                grid-auto-flow: column;

                [class^='image-'],
                [class*=' image-'] {

                    background: no-repeat center;
                    background-size: cover;

                    height: 100%;
                }

                .image-1 {
                    grid-area: image-1;
                }

                .image-2 {
                    grid-area: image-2;
                }

                .image-3 {
                    grid-area: image-3;
                }

                .image-4 {
                    grid-area: image-4;
                }
            }

            .stats {
                grid-area: stats;
                display: grid;
                grid-template-columns: 1fr 1fr 1fr;
                grid-gap: 16px;

                @media only screen and (max-width: $screen-md-max) {
                    grid-template-columns: 1fr 1fr 1fr 1fr;
                }

                @media only screen and (max-width: $screen-xs-max) {
                    grid-template-columns: 1fr 1fr;
                    margin-bottom: 32px;
                }
            }

            .fun-fact {
                width: 75%;
                margin: 8px auto 0;
                align-self: center;
                justify-self: center;

                @media only screen and (max-width: $screen-md-max) {
                    width: 90%;
                    margin: 0 auto 0;
                }

                .author {
                    display: block;
                    padding: 12px 0;
                    color: #FFF;
                }
            }
        }
    }
</style>
